<template>
    <modal-controller-base contentClass="light-light-background purple-purple-color p-relative">
        <template #header>
            <custom-header back-button :title="$t('titleRoute.promotionDetails')">
                <template #start>
                    <ion-button
                        @click="modalController.dismiss()"
                        class="purple-light-color"
                    >
                        <ion-icon :icon="chevronBack"/>
                        {{ $t('button.back') }}
                    </ion-button>
                </template>
            </custom-header>
        </template>

        <button-navigations
            v-if="!isApp()"
            :is-button-back="isButtonBack"
            :is-button-forward="isButtonForward"
            @foward="goFoward"
            @back="goBack"
        />

        <promotion-product
            v-if="currentProduct"
            :product="currentProduct"
            @back="modalController.dismiss"
            @addCart="addToCart"
        />
    </modal-controller-base>
</template>

<script setup lang="ts">
import { alertController, modalController, IonButton, IonIcon } from "@ionic/vue";
import { ref, computed } from "vue";
import { route } from "@/router";
import { t } from "@/plugins/vue-i18n";

import ModalControllerBase from "@/components/modal/modalControllerModals/ModalControllerBase.vue";
import PromotionProduct from "@/components/modal/PromotionProduct.vue";
import CustomHeader from "@/components/basePageElements/CustomHeader.vue";
import ButtonNavigations from "@/components/button/ButtonNavigations.vue";

import { useListStore } from "@/stores/lists";
import { useSupplierStore } from "@/stores/suppliers";
import { useNotificationStore } from "@/stores/notification";

import { interactionFeedbackToast } from "@/functions/baseToast";
import { popAlertOk } from "@/functions/alert";
import { isApp } from "@/functions/enviroment";

import { Promotion, PromotionOld } from "@/types/notification";
import { Supplier } from "@/types/supplier";
import { ICart } from "@/types/order";
import { IonAlertConfig } from "@/types/ionic";

import { chevronBack } from "ionicons/icons";
import DashboardPromotionProduct from "@/components/modal/modalControllerModals/DashboardPromotionProduct.vue";
import { TRACKING_EVENT } from "@/types/enum/notification";

const listStore = useListStore()
const notificationStore = useNotificationStore()

const locationId = computed<string>(() => String(route.value.params.locationId ?? ''))
const isButtonBack = computed<boolean>(() => notificationStore.currentInformationIndex > 0)
const isButtonForward = computed<boolean>(() => notificationStore.currentInformationIndex < (notificationStore.allPromotions.length - 1))
const currentProduct = computed<PromotionOld | undefined>(() => {
    if (!notificationStore.currentPromotion || notificationStore.currentPromotion.type === 'new') return undefined
    return notificationStore.currentPromotion.item
})

const addToCart = async (data: ICart): Promise<void> => {
    if (!data.product || !currentProduct.value) return;

    if (!currentProduct.value.supplierIds?.length) return await popAlertOk({
        message: t('alert.noSupplier')
    })

    const supplierList: Supplier[] = []

    for (const supplierId of currentProduct.value.supplierIds) {
        const supplier = useSupplierStore().suppliers.data.find((supplier: Supplier) => supplier.id === supplierId)
        if (!supplier) continue
        supplierList.push(supplier)
    }

    if (supplierList.length === 1) return addItemToCart(supplierList[0].id, data)

    const params: IonAlertConfig = {
        header: t('alert.selectSupplier'),
        buttons: [{
            text: t('button.ok'),
            handler: (value: string) => {
                if (!value) return false
                addItemToCart(value, data)
            }
        }],
        cssClass: 'custom-alert',
        inputs: [],
    }

    for (const supplier of supplierList) {
        if (supplier.id) params.inputs.push({
            label: supplier.name,
            type: 'radio',
            value: supplier.id
        })
    }
    const alert = await alertController.create(params);
    await alert.present();
}

const addItemToCart = async (supplierId: string, data: ICart): Promise<void> => {
    if (!data.product) return;

    await modalController.dismiss()

    const response = await listStore.addItemToCart(locationId.value, String(data.product.supplierItemId ?? ''), data.amount, supplierId)

    await listStore.getShoppingCart(locationId.value)

    if (response) await interactionFeedbackToast(response, {
        type: 'addCart',
        id: 'add-product-cart'
    }, {
        cssClass: 'toast-offset-50'
    })
}

const openOldPromotion = async () => {
    await (await modalController.create({
        component: DashboardPromotionProduct
    })).present()
}

const goBack = async (): Promise<void> => {
    if (isButtonBack.value) {
        const previousElement = notificationStore.allPromotions[notificationStore.currentInformationIndex - 1]

        if (!previousElement) return

        if (previousElement.type === 'new') {
            await modalController.dismiss()
            notificationStore.currentInformationIndex -= 1
            await openOldPromotion()
            return
        }

        notificationStore.currentInformationIndex -= 1
    }
}

const goFoward = async (): Promise<void> => {
    if (isButtonForward.value) {
        const nextElement = notificationStore.allPromotions[notificationStore.currentInformationIndex + 1]

        if (!nextElement) return

        if (nextElement.type === 'new') {
            await modalController.dismiss()
            notificationStore.currentInformationIndex += 1
            await openOldPromotion()
            return
        }

        notificationStore.currentInformationIndex += 1
    }
}
</script>
